import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
const chapters = [{
  icon: '🧠',
  title: 'El Diagnóstico del Estrés Transmitido',
  desc: 'Cómo la inflación, la inseguridad y la incertidumbre argentina se filtran en tu pareja — y cómo dejar de proyectar eso sobre el otro.',
  tag: 'Capítulo 1'
}, {
  icon: '⏱️',
  title: 'La Tregua de los 5 Minutos',
  desc: 'El Protocolo S.O.S para salir de la escalada antes del punto de no retorno. Incluye la "palabra clave" que para cualquier pelea en segundos.',
  tag: 'Capítulo 2'
}, {
  icon: '📊',
  title: 'El Método del Tablero Logístico',
  desc: 'Gestionar la casa como una empresa sin matar el deseo. Una reunión semanal de 20 min que elimina el resentimiento por asimetría.',
  tag: 'Capítulo 3'
}, {
  icon: '🎒',
  title: 'Auditoría de Resentimientos + Intimidad en Crisis',
  desc: 'Vaciar la mochila emocional de las "facturas viejas". La Regla de los 3 Abrazos de 20 segundos respaldada por ciencia.',
  tag: 'Capítulo 4 & 5'
}, {
  icon: '📜',
  title: 'El Contrato de Convivencia en Crisis',
  desc: 'El documento firmado que protege tu pareja mientras navegás la tormenta. Incluye plantilla lista para completar esta misma noche.',
  tag: 'Capítulo Final'
}];
const zombieTerms = [{
  term: 'Zombie del Estrés',
  def: 'Cansancio tan profundo que no sentís ni amor ni odio. Señal de alerta.'
}, {
  term: 'Escalada Simétrica',
  def: 'Cuando uno grita y el otro grita más fuerte. Nadie gana, ambos quedan destrozados.'
}, {
  term: 'Carga Mental',
  def: 'El esfuerzo invisible de planificar todo. Generalmente cae sobre una sola persona.'
}, {
  term: 'Estrés Transmitido',
  def: 'El contexto externo (inflación, trabajo) que volcás sobre tu pareja porque no tiene otro destino.'
}];
export function UniqueMechanismSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="w-full py-24 md:py-32 bg-[#110038]" data-id="element-339">
      <div className="max-w-5xl mx-auto px-6" data-id="element-340">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 30
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} data-id="element-341">
          {/* Author note - humaniza el producto */}
          <div className="bg-[#1A0050] border-l-4 border-[#E5A83B] rounded-r-2xl p-6 md:p-8 mb-16 relative" data-id="element-342">
            <div className="absolute -top-3 left-6 bg-[#E5A83B] text-black text-xs font-bold px-3 py-1 rounded-full" data-id="element-343">
              NOTA DEL AUTOR
            </div>
            <p className="text-[#C4B5FD] text-lg leading-relaxed italic font-sans" data-id="element-344">
              "Conozco perfectamente el sonido del silencio tenso en la cena
              después de que uno menciona que las expensas subieron un 40%.
              Conozco la taquicardia cuando escuchás la llave en la cerradura y,
              en lugar de alegría, sentís una pesadez en el pecho.
              <br data-id="element-345" />
              <br data-id="element-346" />
              En Argentina, el estrés no es solo personal:{' '}
              <strong className="text-white not-italic" data-id="element-347">
                es ambiental.
              </strong>{' '}
              Lo que vas a leer no es teoría romántica. Es un protocolo de
              primeros auxilios para los que están cansados pero todavía les
              importa."
            </p>
          </div>

          <div className="text-center mb-16" data-id="element-348">
            <div className="inline-block bg-[#34D399]/20 text-[#34D399] px-4 py-1.5 rounded-full text-sm font-bold tracking-wide mb-6" data-id="element-349">
              LO QUE HAY DENTRO
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-serif" data-id="element-350">
              5 capítulos. Un plan de acción.
            </h2>
            <p className="text-[#C4B5FD] text-lg mt-6 max-w-2xl mx-auto" data-id="element-351">
              No es teoría aburrida. Es un{' '}
              <strong data-id="element-352">protocolo de emergencia</strong> diseñado específicamente
              para parejas argentinas en crisis.
            </p>
          </div>

          {/* Chapters list */}
          <div className="space-y-4 mb-20" data-id="element-353">
            {chapters.map((ch, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: -20
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.5,
            delay: index * 0.1,
            ease: 'easeOut'
          }} className="bg-[#E0D7FF] rounded-2xl p-6 flex gap-5 items-start" data-id="element-354">
                <div className="text-4xl flex-shrink-0 w-12 flex justify-center" data-id="element-355">
                  {ch.icon}
                </div>
                <div className="flex-1" data-id="element-356">
                  <div className="text-[#7C3AED] text-xs font-bold tracking-wider mb-1" data-id="element-357">
                    {ch.tag}
                  </div>
                  <h3 className="text-[#1E0A3C] font-bold text-lg font-serif mb-1" data-id="element-358">
                    {ch.title}
                  </h3>
                  <p className="text-[#3B1F6E] text-sm font-sans leading-relaxed" data-id="element-359">
                    {ch.desc}
                  </p>
                </div>
                <div className="text-[#34D399] text-xl flex-shrink-0" data-id="element-360">✔</div>
              </motion.div>)}
          </div>

          {/* "¿Te suena?" glosario de crisis */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} transition={{
          duration: 0.7,
          delay: 0.6
        }} data-id="element-361">
            <p className="text-center text-[#A78BFA] font-bold text-sm tracking-wider uppercase mb-6" data-id="element-362">
              ¿Te identificás con alguno de estos estados?
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-363">
              {zombieTerms.map((item, i) => <div key={i} className="bg-[#0D002B] border border-[#7C3AED]/30 rounded-xl p-4" data-id="element-364">
                  <p className="text-[#E5A83B] font-bold text-sm mb-1" data-id="element-365">
                    "{item.term}"
                  </p>
                  <p className="text-[#C4B5FD] text-sm font-sans" data-id="element-366">{item.def}</p>
                </div>)}
            </div>
            <p className="text-center text-[#A78BFA] text-sm mt-4 italic" data-id="element-367">
              👉 Si te reconocés en alguno, este material es para vos.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}